import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { ControllerRenderProps } from "react-hook-form";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import type { IContactInfo } from "@b2bportal/core-types";
import clsx from "clsx";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import styles from "./PhoneNumber.module.scss";

export interface PhoneNumberProps
  extends PlatformComponentProps,
    Omit<ControllerRenderProps<IContactInfo, "phoneNumber">, "ref"> {
  error?: boolean;
  placeholder?: string;
  defaultValue?: string;
  countryCode?: string;
}

const formatPhoneForDisplay = (digits: string, countryCode: string): string => {
  if (!digits) return "";

  // Validate country code format before parsing
  if (!countryCode || !countryCode.startsWith("+")) {
    return digits;
  }

  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    // Get region code from country code (e.g., +44 -> GB)
    const countryCodeNumber = parseInt(countryCode.substring(1), 10);

    // Validate country code number is valid
    if (isNaN(countryCodeNumber)) {
      return digits;
    }

    const regionCode = phoneUtil.getRegionCodeForCountryCode(countryCodeNumber);

    // Parse and format the number
    const parsedNumber = phoneUtil.parse(digits, regionCode);
    // Use NATIONAL format (no country code prefix in display)
    return phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL);
  } catch (e) {
    // If parsing fails, return unformatted digits
    return digits;
  }
};

export const PhoneNumber = forwardRef<HTMLInputElement, PhoneNumberProps>(
  (props, formRef) => {
    const {
      className,
      placeholder,
      onChange,
      countryCode = "+1",
      error: _error,
      ...field
    } = props;

    // Format the initial value
    const getFormattedValue = (value: string, code: string) => {
      if (!value) return "";
      const cleanedValue = value.replace(/\D/g, ""); // Remove non-digits
      if (!cleanedValue) return "";
      return formatPhoneForDisplay(cleanedValue, code);
    };

    const [display, setDisplay] = useState(
      getFormattedValue(field.value, countryCode)
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPos = useRef<number>(0);

    const mergedRef = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof formRef === "function") formRef(el);
        else if (formRef) formRef.current = el;
      },
      [formRef]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const cursorInRaw = e.target.selectionStart ?? raw.length;

      // Count digits before cursor in raw input
      let digitsBefore = 0;
      for (let i = 0; i < cursorInRaw; i++) {
        if (/\d/.test(raw[i])) digitsBefore++;
      }

      const digitsOnly = raw.replace(/\D/g, ""); // Strip formatting
      const formatted = formatPhoneForDisplay(digitsOnly, countryCode);

      // Find cursor position in formatted string
      let pos = 0;
      let counted = 0;
      for (pos = 0; pos < formatted.length; pos++) {
        if (counted === digitsBefore) break;
        if (/\d/.test(formatted[pos])) counted++;
      }

      cursorPos.current = pos;
      setDisplay(formatted);
      onChange(digitsOnly);
    };

    // Restore cursor position after formatting
    useEffect(() => {
      if (inputRef.current == null) return;
      if (document.activeElement === inputRef.current) {
        inputRef.current.setSelectionRange(
          cursorPos.current,
          cursorPos.current
        );
      }
    }, [display]);

    // Update display when country code changes
    useEffect(() => {
      const currentValue = field.value || "";
      const cleanedValue = currentValue.replace(/\D/g, "");
      if (cleanedValue) {
        const formatted = formatPhoneForDisplay(cleanedValue, countryCode);
        setDisplay(formatted);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryCode]);

    const [block] = useModuleBEM(styles, "PhoneNumber");

    return (
      <input
        {...field}
        ref={mergedRef}
        type="tel"
        className={clsx(block, className)}
        placeholder={placeholder}
        onChange={handleChange}
        value={display}
        autoComplete="tel"
        maxLength={20}
      />
    );
  }
);
PhoneNumber.displayName = "PhoneNumber";
